import React, { useEffect, useState, Children } from 'react';
export function PrivacyPolicy() {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);
  return <div className="min-h-screen bg-white text-black transition-colors duration-500" data-id="element-410">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full bg-white/80 backdrop-blur-md border-b border-black/5 z-50" data-id="element-411">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-412">
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity" data-id="element-413">
            <img src="https://cdn.magicpatterns.com/uploads/fTMKMcwziqc5vCPyjAKBhC/C14.svg" alt="Caltalk" className="h-20 w-auto" data-id="element-414" />
          </a>
          <a href="/" className="text-sm text-black/60 hover:text-coral-600 transition-colors" data-id="element-415">
            ← back to home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className={`pt-32 pb-20 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-416">
        <div className="max-w-4xl mx-auto px-6" data-id="element-417">
          {/* Title */}
          <div className="mb-16" data-id="element-418">
            <h1 className="text-4xl md:text-5xl font-light mb-4" data-id="element-419">
              Privacy Policy
            </h1>
            <p className="text-black/60" data-id="element-420">Last Updated: December 10, 2025</p>
          </div>

          {/* Intro */}
          <div className="prose prose-lg max-w-none mb-12" data-id="element-421">
            <p className="text-lg text-black/80 leading-relaxed" data-id="element-422">
              This Privacy Policy describes how Caltalk, LLC collects, uses,
              processes, shares, and protects Your information when You use the
              Caltalk mobile application, website, and related services
              (collectively, the "Service"). By using the Service, You agree to
              the collection and use of information in accordance with this
              Privacy Policy.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-12" data-id="element-423">
            <Section number="1" title="Interpretation and Definitions" data-id="element-424">
              <Subsection title="1.1 Interpretation" data-id="element-425">
                <p data-id="element-426">
                  Capitalized terms have the meanings assigned to them in this
                  Policy.
                </p>
              </Subsection>

              <Subsection title="1.2 Definitions" data-id="element-427">
                <dl className="space-y-4" data-id="element-428">
                  <Term term="Account" definition="means the unique profile created for You to access the Service." data-id="element-429" />
                  <Term term="Application" definition='means the mobile application titled "Caltalk."' data-id="element-430" />
                  <Term term="Company, We, Us, or Our" definition="means Caltalk, LLC, with mailing address 1221 Brickell Ave Suite 900, Miami, Florida 33131, United States." data-id="element-431" />
                  <Term term="Device" definition="means any device used to access the Service." data-id="element-432" />
                  <Term term="Personal Data" definition="means information that identifies or can reasonably be linked to an individual. Personal Data includes identifiable health information." data-id="element-433" />
                  <Term term="Health Data" definition="means information You provide relating to Your physical characteristics, health, nutrition, goals, or activity, including weight, height, dietary preferences, macro goals, health attributes, and body composition." data-id="element-434" />
                  <Term term="Usage Data" definition="means data collected automatically through use of the Service, including device information, activity logs, and analytics data." data-id="element-435" />
                  <Term term="User Content" definition="means text, meal descriptions, dietary preferences, metadata, or other inputs You provide through the Service." data-id="element-436" />
                  <Term term="Website" definition="means caltalk.ai." data-id="element-437" />
                  <Term term="You" definition="means the individual using the Service or the organization on whose behalf the Service is used." data-id="element-438" />
                </dl>
              </Subsection>
            </Section>

            <Section number="2" title="Types of Data We Collect" data-id="element-439">
              <p className="mb-6" data-id="element-440">
                We collect the categories of data described below. Certain
                categories may qualify as health information, sensitive personal
                information, or biometric-adjacent data under various privacy
                laws.
              </p>

              <Subsection title="2.1 Personal Data You Provide" data-id="element-441">
                <p className="mb-4" data-id="element-442">
                  We may collect the following Personal Data when You create an
                  Account or use the Service:
                </p>
                <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-443">
                  <li data-id="element-444">First name</li>
                  <li data-id="element-445">Last name</li>
                  <li data-id="element-446">Email address</li>
                  <li data-id="element-447">Optional profile details You provide</li>
                </ul>
              </Subsection>

              <Subsection title="2.2 Health and Nutrition Data" data-id="element-448">
                <p className="mb-4" data-id="element-449">
                  To provide conversational meal tracking and restaurant
                  recommendation features, We collect:
                </p>
                <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-450">
                  <li data-id="element-451">Weight</li>
                  <li data-id="element-452">Height</li>
                  <li data-id="element-453">Dietary preferences and restrictions</li>
                  <li data-id="element-454">
                    Daily macro goals (protein, carbohydrates, fats, calories)
                  </li>
                  <li data-id="element-455">Meal descriptions and food logs</li>
                  <li data-id="element-456">Restaurant searches and preferences</li>
                  <li data-id="element-457">Nutrition goals</li>
                  <li data-id="element-458">
                    Any other health information You choose to enter into the
                    Application
                  </li>
                </ul>
                <p className="text-black/80" data-id="element-459">
                  You decide which health information to provide. The Service
                  cannot function properly without basic nutrition data, such as
                  your macro goals and meal information.
                </p>
              </Subsection>

              <Subsection title="2.3 User Content (Including Conversational Data)" data-id="element-460">
                <p className="mb-4" data-id="element-461">User Content includes:</p>
                <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-462">
                  <li data-id="element-463">Text-based conversations about meals and nutrition</li>
                  <li data-id="element-464">Restaurant search queries</li>
                  <li data-id="element-465">Meal descriptions and logs</li>
                  <li data-id="element-466">Dietary preferences and restrictions</li>
                </ul>
                <div className="bg-coral-50/50 border border-coral-500/20 rounded-2xl p-6" data-id="element-467">
                  <p className="font-medium text-coral-700 mb-2" data-id="element-468">Important:</p>
                  <p className="text-black/80" data-id="element-469">
                    We may use anonymized or aggregated conversational data to
                    improve Our AI models, algorithms, and product features. We
                    do not associate such data with Your identity when used for
                    internal development.
                  </p>
                </div>
              </Subsection>

              <Subsection title="2.4 Location Data" data-id="element-470">
                <p className="mb-4" data-id="element-471">
                  With Your permission, We may collect location data to:
                </p>
                <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-472">
                  <li data-id="element-473">Provide restaurant recommendations near You</li>
                  <li data-id="element-474">Suggest relevant dining options</li>
                  <li data-id="element-475">Improve the accuracy of Our Service</li>
                </ul>
                <p className="text-black/80" data-id="element-476">
                  You can control location permissions through Your Device
                  settings.
                </p>
              </Subsection>

              <Subsection title="2.5 Usage Data" data-id="element-477">
                <p className="mb-4" data-id="element-478">We automatically collect:</p>
                <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-479">
                  <li data-id="element-480">Device type, operating system, and app version</li>
                  <li data-id="element-481">IP address</li>
                  <li data-id="element-482">Time stamps and activity logs</li>
                  <li data-id="element-483">Crash data</li>
                  <li data-id="element-484">Feature usage analytics</li>
                  <li data-id="element-485">Performance data</li>
                </ul>
                <p className="text-black/80" data-id="element-486">
                  This data helps Us secure, improve, and optimize the Service.
                </p>
              </Subsection>

              <Subsection title="2.6 Cookies and Tracking Technologies" data-id="element-487">
                <p data-id="element-488">
                  The Website uses cookies, tags, and similar technologies for
                  analytics, functionality, and security. The Application may
                  use mobile identifiers for analytics.
                </p>
              </Subsection>
            </Section>

            <Section number="3" title="How We Use Your Data" data-id="element-489">
              <p className="mb-6" data-id="element-490">
                We use the collected data for the following purposes:
              </p>

              <Subsection title="3.1 To Provide and Improve the Service" data-id="element-491">
                <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-492">
                  <li data-id="element-493">Conversational meal tracking</li>
                  <li data-id="element-494">Restaurant recommendations matching Your macro goals</li>
                  <li data-id="element-495">Nutrition tracking and analysis</li>
                  <li data-id="element-496">Personalization and recommendations</li>
                  <li data-id="element-497">Machine learning model improvement</li>
                  <li data-id="element-498">Account management</li>
                  <li data-id="element-499">Service stability and debugging</li>
                </ul>
              </Subsection>

              <Subsection title="3.2 To Improve AI and Natural Language Models" data-id="element-500">
                <p className="mb-4" data-id="element-501">
                  We use conversational data and related User Content to:
                </p>
                <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-502">
                  <li data-id="element-503">Train and improve AI models</li>
                  <li data-id="element-504">Enhance accuracy of restaurant recommendations</li>
                  <li data-id="element-505">Improve overall product performance</li>
                </ul>
                <p className="text-black/80" data-id="element-506">
                  Such use is strictly anonymized or aggregated and not linked
                  to Your identity.
                </p>
              </Subsection>

              <Subsection title="3.3 Communications" data-id="element-507">
                <p className="mb-4" data-id="element-508">We may contact You about:</p>
                <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-509">
                  <li data-id="element-510">Updates and new features</li>
                  <li data-id="element-511">Service-related notifications</li>
                  <li data-id="element-512">Customer support responses</li>
                </ul>
              </Subsection>

              <Subsection title="3.4 Marketing (Optional)" data-id="element-513">
                <p data-id="element-514">We may send You promotional emails unless You opt out.</p>
              </Subsection>

              <Subsection title="3.5 Legal and Compliance" data-id="element-515">
                <p className="mb-4" data-id="element-516">We may use Your data to:</p>
                <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-517">
                  <li data-id="element-518">Detect or prevent fraud</li>
                  <li data-id="element-519">Comply with law enforcement obligations</li>
                  <li data-id="element-520">Protect Our rights</li>
                </ul>
              </Subsection>

              <Subsection title="3.6 Business Transfers" data-id="element-521">
                <p data-id="element-522">
                  If We engage in a merger, acquisition, financing, or asset
                  sale, Your information may be transferred as permitted by law.
                </p>
              </Subsection>
            </Section>

            <Section number="4" title="Legal Bases for Processing (For GDPR-Covered Users)" data-id="element-523">
              <p className="mb-4" data-id="element-524">Where applicable, We rely on:</p>
              <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-525">
                <li data-id="element-526">Consent</li>
                <li data-id="element-527">Contract performance</li>
                <li data-id="element-528">
                  Legitimate interests (e.g., service improvement, security)
                </li>
                <li data-id="element-529">Legal obligations</li>
              </ul>
            </Section>

            <Section number="5" title="How We Share Personal Data" data-id="element-530">
              <p className="mb-6" data-id="element-531">We may share Personal Data:</p>

              <Subsection title="5.1 Service Providers" data-id="element-532">
                <p className="mb-4" data-id="element-533">
                  With vendors who support the Service, such as:
                </p>
                <ul className="space-y-2 list-disc list-inside text-black/70" data-id="element-534">
                  <li data-id="element-535">Cloud hosting</li>
                  <li data-id="element-536">AI and natural language processing providers</li>
                  <li data-id="element-537">Analytics providers</li>
                  <li data-id="element-538">Customer support tools</li>
                  <li data-id="element-539">Restaurant data providers</li>
                </ul>
              </Subsection>

              <Subsection title="5.2 Business Transfers" data-id="element-540">
                <p data-id="element-541">As part of corporate transactions.</p>
              </Subsection>

              <Subsection title="5.3 Legal Compliance" data-id="element-542">
                <p data-id="element-543">With law enforcement or regulators when legally required.</p>
              </Subsection>

              <Subsection title="5.4 With Your Consent" data-id="element-544">
                <p data-id="element-545">Where You direct Us to share information.</p>
              </Subsection>

              <Subsection title="5.5 Aggregated or Anonymized Data" data-id="element-546">
                <p className="mb-4" data-id="element-547">
                  We may share aggregated or non-identifiable data for
                  analytics, research, or business purposes. We do not sell Your
                  Personal Data.
                </p>
              </Subsection>
            </Section>

            <Section number="6" title="User Content and License Rights" data-id="element-548">
              <p className="mb-4" data-id="element-549">
                By submitting User Content, You grant the Company a worldwide,
                royalty-free, sublicensable license to use, store, process,
                reproduce, modify, and create derivative works from Your User
                Content solely to operate, maintain, and improve the Service,
                including internal AI model training.
              </p>
              <p data-id="element-550">
                We do not publicly display Your conversational data or personal
                information without Your express consent.
              </p>
            </Section>

            <Section number="7" title="Retention of Data" data-id="element-551">
              <p className="mb-4" data-id="element-552">We retain:</p>
              <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-553">
                <li data-id="element-554">
                  Personal Data for as long as Your Account is active or
                  necessary for the purposes described
                </li>
                <li data-id="element-555">Health Data until You delete it or delete Your Account</li>
                <li data-id="element-556">
                  Usage Data for a shorter period unless needed for security
                </li>
                <li data-id="element-557">
                  Backups for limited periods as part of standard retention
                  practices
                </li>
              </ul>
              <p data-id="element-558">
                We delete or anonymize data when no longer needed unless legally
                required to retain it.
              </p>
            </Section>

            <Section number="8" title="Data Transfers" data-id="element-559">
              <p data-id="element-560">
                Your information may be transferred to and processed on servers
                outside Your jurisdiction. We implement safeguards as required
                by law, including standard contractual clauses for international
                transfers.
              </p>
            </Section>

            <Section number="9" title="Your Privacy Rights" data-id="element-561">
              <p className="mb-4" data-id="element-562">
                Depending on Your jurisdiction, You may have rights to:
              </p>
              <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-563">
                <li data-id="element-564">Access Your Personal Data</li>
                <li data-id="element-565">Correct inaccurate data</li>
                <li data-id="element-566">Delete Personal Data</li>
                <li data-id="element-567">Export Your data</li>
                <li data-id="element-568">Withdraw consent</li>
                <li data-id="element-569">Object to processing</li>
                <li data-id="element-570">Request restriction of processing</li>
              </ul>
              <p data-id="element-571">
                You may exercise rights through in-app settings or by contacting
                Us at info@caltalk.ai.
              </p>
            </Section>

            <Section number="10" title="Deleting Your Personal Data" data-id="element-572">
              <p className="mb-4" data-id="element-573">You may request deletion of:</p>
              <ul className="space-y-2 list-disc list-inside text-black/70 mb-4" data-id="element-574">
                <li data-id="element-575">Account</li>
                <li data-id="element-576">Meal logs and conversational history</li>
                <li data-id="element-577">Profile data</li>
              </ul>
              <p data-id="element-578">Some data may persist where legally required.</p>
            </Section>

            <Section number="11" title="Security" data-id="element-579">
              <p data-id="element-580">
                We use administrative, technical, and physical safeguards to
                protect Your data. However, no online system is entirely secure.
              </p>
            </Section>

            <Section number="12" title="Children's Privacy" data-id="element-581">
              <p data-id="element-582">
                The Service is not intended for children under 13. We do not
                knowingly collect personal information from children under 13.
              </p>
            </Section>

            <Section number="13" title="Third-Party Links" data-id="element-583">
              <p data-id="element-584">
                The Service may contain links to third-party websites or
                services. Their privacy practices are their own, and We do not
                control or endorse them.
              </p>
            </Section>

            <Section number="14" title="Changes to This Privacy Policy" data-id="element-585">
              <p data-id="element-586">
                We may update this Policy from time to time. Updates become
                effective when posted. We may notify You by email or through the
                Application for material changes.
              </p>
            </Section>

            <Section number="15" title="Contact Us" data-id="element-587">
              <p className="mb-4" data-id="element-588">
                If You have questions or requests related to this Privacy
                Policy, You may contact Us:
              </p>
              <div className="bg-black/5 rounded-2xl p-6 space-y-2" data-id="element-589">
                <p data-id="element-590">
                  <span className="font-medium" data-id="element-591">Email:</span>{' '}
                  <a href="mailto:info@caltalk.ai" className="text-coral-600 hover:underline" data-id="element-592">
                    info@caltalk.ai
                  </a>
                </p>
                <p data-id="element-593">
                  <span className="font-medium" data-id="element-594">Mailing Address:</span>
                  <br data-id="element-595" />
                  Caltalk, LLC
                  <br data-id="element-596" />
                  1221 Brickell Ave Suite 900
                  <br data-id="element-597" />
                  Miami, Florida 33131
                  <br data-id="element-598" />
                  United States
                </p>
              </div>
            </Section>
          </div>
        </div>
      </main>
    </div>;
}
interface SectionProps {
  number: string;
  title: string;
  children: React.ReactNode;
}
function Section({
  number,
  title,
  children
}: SectionProps) {
  return <section className="scroll-mt-32" data-id="element-599">
      <h2 className="text-2xl font-light mb-6 flex items-baseline gap-3" data-id="element-600">
        <span className="text-coral-600" data-id="element-601">{number}.</span>
        <span data-id="element-602">{title}</span>
      </h2>
      <div className="text-black/70 leading-relaxed space-y-4" data-id="element-603">{children}</div>
    </section>;
}
interface SubsectionProps {
  title: string;
  children: React.ReactNode;
}
function Subsection({
  title,
  children
}: SubsectionProps) {
  return <div className="mt-6" data-id="element-604">
      <h3 className="text-lg font-medium mb-4 text-black/90" data-id="element-605">{title}</h3>
      <div className="text-black/70 leading-relaxed" data-id="element-606">{children}</div>
    </div>;
}
interface TermProps {
  term: string;
  definition: string;
}
function Term({
  term,
  definition
}: TermProps) {
  return <div data-id="element-607">
      <dt className="font-medium text-black/90 mb-1" data-id="element-608">{term}</dt>
      <dd className="text-black/70 ml-4" data-id="element-609">{definition}</dd>
    </div>;
}